import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Copy, Ticket, Wallet, Users, Coins, TrendingUp, ChevronRight, CheckCircle2, ExternalLink, AlertTriangle, Loader2, ShieldAlert, RefreshCw, Crown } from "lucide-react";
import { connectWallet, currentChainId, switchToChain, sendUsdtTransfer } from "@/lib/wallet";
import { formatUsdt, UsdtAmount } from "@/lib/format";

// ---------------------------------------------------------------------------
// Small local hook: turn a fire-and-forget `api.get(...).then(setState)` into a
// proper load / error / retry cycle. Prevents the "stuck on Loading… forever"
// state when a transient error happens.
// ---------------------------------------------------------------------------
function useDashboardFetch(loader) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await loader();
      setData(d);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail, "Couldn't load — please retry."));
    } finally {
      setLoading(false);
    }
    // loader is intentionally referenced but callers pass a stable inline fn per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { run(); }, [run]);
  return { data, error, loading, refetch: run, setData };
}

function LoadErrorState({ error, onRetry, testid = "load-error" }) {
  return (
    <div className="crown-card p-6 flex items-start gap-4" data-testid={testid}>
      <AlertTriangle className="h-5 w-5 mt-0.5 text-amber-400 shrink-0" />
      <div className="flex-1">
        <div className="font-medium text-slate-100">{error}</div>
        <div className="text-xs text-slate-400 mt-1">This is usually transient. Try again — if it keeps happening, sign out and back in.</div>
      </div>
      <Button variant="outline" onClick={onRetry} data-testid={`${testid}-retry`}>
        <RefreshCw className="h-4 w-4 mr-1" /> Retry
      </Button>
    </div>
  );
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="crown-card p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="mt-2 stat-number">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active: "bg-emerald-100 text-emerald-800",
    paid: "bg-emerald-100 text-emerald-800",
    approved: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-900",
    verifying: "bg-amber-100 text-amber-900",
    under_review: "bg-amber-100 text-amber-900",
    submitted: "bg-blue-100 text-blue-800",
    processing: "bg-blue-100 text-blue-800",
    open: "bg-blue-100 text-blue-800",
    failed: "bg-red-100 text-red-800",
    rejected: "bg-red-100 text-red-800",
    refunded: "bg-[#1B3455]/70 text-slate-200",
    cancelled: "bg-[#1B3455]/70 text-slate-200",
    reversed: "bg-[#1B3455]/70 text-slate-200",
    withdrawn: "bg-[#1B3455]/70 text-slate-200",
    closed: "bg-[#1B3455]/70 text-slate-200",
    not_submitted: "bg-[#1B3455]/70 text-slate-200",
  };
  return <span className={`chip ${map[status] || "bg-[#1B3455]/60 text-slate-200"}`}>{status?.replaceAll("_", " ")}</span>;
}

export function Overview() {
  const { user } = useAuth();
  const { data: d, error, loading, refetch } = useDashboardFetch(
    async () => (await api.get("/me/dashboard")).data
  );

  if (loading && !d) return <DashboardShell><div className="text-slate-400" data-testid="overview-loading">Loading...</div></DashboardShell>;
  if (error && !d) return <DashboardShell><LoadErrorState error={error} onRetry={refetch} testid="overview-error" /></DashboardShell>;

  const referralLink = `${window.location.origin}/register?ref=${user?.referral_id || ""}`;

  return (
    <DashboardShell>
      <div className="mb-8">
        <div className="overline">Welcome back</div>
        <h1 className="font-serif text-4xl mt-1" data-testid="dashboard-greeting">Hello, {d.profile.full_name}.</h1>
      </div>

      {/* Premium Crown Coupon Membership Card */}
      <div className="mb-10" data-testid="crown-coupon-card">
        <div className="relative overflow-hidden rounded-2xl shadow-[0_20px_60px_-20px_rgba(42,51,26,0.35)] border border-[#D4AF37]/40">
          {/* Base olive layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#3B4A1F] via-[#556B2F] to-[#2A331A]" aria-hidden="true"></div>
          {/* Subtle grain / grid overlay */}
          <div className="absolute inset-0 opacity-[0.10]"
               style={{ backgroundImage: "linear-gradient(rgba(212,175,55,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.6) 1px, transparent 1px)", backgroundSize: "44px 44px" }}
               aria-hidden="true"></div>
          {/* Gold sheen */}
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#D4AF37]/25 blur-3xl" aria-hidden="true"></div>
          <div className="absolute -bottom-20 -left-16 h-48 w-48 rounded-full bg-[#D4AF37]/10 blur-3xl" aria-hidden="true"></div>

          <div className="relative p-8 sm:p-10 lg:p-12 text-white">
            <div className="flex items-start justify-between gap-6">
              <div className="flex items-center gap-4">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-white/10 border border-[#D4AF37]/40 backdrop-blur-sm">
                  <Crown className="h-7 w-7 text-[#D4AF37]" strokeWidth={2.2} />
                </span>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">Premium Membership</div>
                  <div className="font-serif text-3xl sm:text-4xl leading-none mt-1">Crown <span className="text-[#D4AF37]">Coupon</span></div>
                </div>
              </div>
              <div className="hidden sm:block text-right">
                <div className="text-[11px] tracking-[0.22em] uppercase text-white/60">Network</div>
                <div className="text-sm font-medium mt-1">BNB Smart Chain</div>
                <div className="text-[11px] text-white/70 mt-0.5">USDT · BEP-20</div>
              </div>
            </div>

            <div className="my-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent"></div>

            <div className="grid sm:grid-cols-2 gap-8 items-end">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-white/60">Total Supply</div>
                <div className="font-serif text-4xl sm:text-5xl leading-none tabular mt-2" data-testid="cc-card-supply">
                  ONLY 10 <span className="text-[#D4AF37]">MILLION</span>
                </div>
                <div className="text-sm text-white/70 mt-2">Coupons — numbered CC-00000001 onward</div>
              </div>
              <div className="sm:text-right">
                <div className="text-[11px] tracking-[0.22em] uppercase text-white/60">1 Coupon Price</div>
                <div className="font-serif text-4xl sm:text-5xl leading-none tabular mt-2" data-testid="cc-card-price">
                  1,000 <span className="text-[#D4AF37]">USDT</span>
                </div>
                <div className="text-sm text-white/70 mt-2">Only. No hidden fees.</div>
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between gap-6">
              <div className="flex items-center gap-2 text-xs text-white/60">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37]"></span>
                <span className="tracking-wider">CROWN · COUPON · MEMBER · PASS</span>
              </div>
              <Link to="/app/buy">
                <Button className="bg-[#D4AF37] hover:bg-[#B8860B] text-[#2A331A] font-semibold h-11 px-6" data-testid="cc-card-cta">
                  Get your Crown Coupon <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="crown-card p-6">
          <div className="overline">Your profile</div>
          <div className="mt-3 space-y-2 text-sm">
            <div><span className="text-slate-400">User ID:</span> <span className="font-medium tabular" data-testid="profile-user-id">{d.profile.id}</span></div>
            <div><span className="text-slate-400">Referral ID:</span> <span className="font-medium tabular" data-testid="profile-referral-id">{d.profile.referral_id}</span></div>
            <div><span className="text-slate-400">Email:</span> {d.profile.email}</div>
            <div><span className="text-slate-400">Mobile:</span> {d.profile.mobile || "—"}</div>
            <div><span className="text-slate-400">KYC:</span> <StatusChip status={d.profile.kyc_status} /></div>
          </div>
        </div>

        <div className="crown-card p-6">
          <div className="overline">Referral link</div>
          <div className="mt-3 flex gap-2">
            <Input readOnly value={referralLink} className="text-xs" data-testid="referral-link-input" />
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(referralLink); toast.success("Copied!"); }} data-testid="btn-copy-referral"><Copy className="h-4 w-4" /></Button>
          </div>
          <div className="mt-4 text-sm text-slate-300">Invite people to Crown Coupon. You earn commissions only when they complete a genuine purchase.</div>
          <div className="mt-4"><Link to="/app/buy"><Button className="bg-[#3B4A1F] hover:bg-[#212814] text-white" data-testid="btn-cta-buy">Buy your Crown Coupon <ChevronRight className="ml-1 h-4 w-4" /></Button></Link></div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-total-referrals" label="Total Referrals" value={d.stats.total_referrals} />
        <StatCard testid="stat-completed-sales" label="Completed Sales" value={d.stats.completed_referred_sales} />
        <StatCard testid="stat-active-coupons" label="Active Coupons" value={d.stats.active_coupons} />
        <StatCard testid="stat-total-earned" label="Total Earned" value={formatUsdt(d.stats.total_earned_commission)} />
        <StatCard testid="stat-pending" label="Pending" value={formatUsdt(d.stats.pending_commission, { withSymbol: false })} sub="USDT" />
        <StatCard testid="stat-approved" label="Approved" value={formatUsdt(d.stats.approved_commission, { withSymbol: false })} sub="USDT" />
        <StatCard testid="stat-withdrawn" label="Withdrawn" value={formatUsdt(d.stats.withdrawn_commission, { withSymbol: false })} sub="USDT" />
        <StatCard testid="stat-balance" label="Available" value={formatUsdt(d.stats.approved_commission, { withSymbol: false })} sub="USDT (approved)" />
      </div>

      {d.activity && (
        <div className="mb-8" data-testid="activity-count-widget">
          <div className="crown-card p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-l-4 border-[#D4AF37]/60">
            <div>
              <div className="overline">Activity Count</div>
              <div className="mt-2 font-serif text-4xl tabular text-white" data-testid="activity-count-value">
                {Number(d.activity.value ?? 0).toLocaleString()}
              </div>
              <div className="text-xs text-slate-400 mt-2">
                Community-wide activity indicator · display only
              </div>
            </div>
            <div className="sm:text-right">
              <div className="overline">Next Update</div>
              <div className="mt-2 font-serif text-2xl tabular text-white" data-testid="activity-next-update">
                {Number(d.activity.next_update_days ?? 0)} days
              </div>
              <div className="text-xs text-slate-400 mt-2">Refreshed by the Crown Coupon team</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="crown-card p-6">
          <div className="flex items-center justify-between mb-3">
            <div><div className="overline">Active Crown Coupons</div><div className="font-serif text-2xl">{d.coupons.filter(c => c.status === "active").length}</div></div>
            <Ticket className="h-6 w-6 text-[#556B2F]" />
          </div>
          <Table>
            <TableHeader>
              <TableRow><TableHead>Coupon ID</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {d.coupons.length === 0 && <TableRow><TableCell colSpan={3} className="text-slate-400 text-center py-6">No coupons yet</TableCell></TableRow>}
              {d.coupons.slice(0, 5).map(c => (
                <TableRow key={c.id}><TableCell className="font-medium tabular">{c.id}</TableCell><TableCell className="text-xs text-slate-400">{new Date(c.purchase_date).toLocaleDateString()}</TableCell><TableCell><StatusChip status={c.status} /></TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="crown-card p-6">
          <div className="overline mb-3">Recent Commissions</div>
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Level</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.recent_commissions.length === 0 && <TableRow><TableCell colSpan={4} className="text-slate-400 text-center py-6">No commissions yet</TableCell></TableRow>}
              {d.recent_commissions.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="text-xs">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>L{c.level}</TableCell>
                  <TableCell className="tabular"><UsdtAmount value={c.commission_amount} /></TableCell>
                  <TableCell><StatusChip status={c.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardShell>
  );
}

export function BuyCoupon() {
  const [cfg, setCfg] = useState(null);
  const [variant, setVariant] = useState("1000"); // "1000" | "100" | "50"
  const [order, setOrder] = useState(null);
  const [wallet, setWallet] = useState("");
  const [connectedChain, setConnectedChain] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | connecting | signing | verifying | paid | failed
  const [txHash, setTxHash] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [timeLeft, setTimeLeft] = useState(null);
  const [coupon, setCoupon] = useState(null);

  const priceForVariant = variant === "50" ? 50 : (variant === "100" ? 100 : (cfg?.price_usdt || 1000));

  useEffect(() => {
    api.get("/public/payment-config").then(({ data }) => setCfg(data));
    if (window.ethereum) {
      window.ethereum.on?.("accountsChanged", (a) => setWallet(a?.[0] || ""));
      window.ethereum.on?.("chainChanged", (c) => setConnectedChain(parseInt(c, 16)));
    }
  }, []);

  // Order expiration countdown
  useEffect(() => {
    if (!order?.expires_at) return;
    const t = setInterval(() => {
      const ms = new Date(order.expires_at).getTime() - Date.now();
      setTimeLeft(Math.max(0, Math.floor(ms / 1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [order?.expires_at]);

  // Poll order status once submitted (for confirmations)
  useEffect(() => {
    if (!order?.id || phase !== "verifying") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/coupons/order/${order.id}`);
        setOrder(data.order);
        if (data.order.status === "paid") {
          setPhase("paid");
          setCoupon(data.coupon);
          setStatusMsg("Payment confirmed — Crown Coupon issued");
          toast.success("Payment confirmed!");
          clearInterval(t);
        } else if (data.order.status === "failed") {
          setPhase("failed");
          setStatusMsg(data.order.reason || "Verification failed");
          clearInterval(t);
        } else if (data.order.test_mode) {
          setStatusMsg("Test mode — awaiting admin verification");
        } else {
          // Ask backend to recheck on-chain (production)
          try {
            const { data: r } = await api.post(`/coupons/order/${order.id}/recheck`);
            if (r.status === "paid") { setPhase("paid"); setCoupon(null); toast.success("Confirmed"); clearInterval(t); }
            else if (r.confirmations !== undefined) setStatusMsg(`Confirmations: ${r.confirmations} / ${cfg?.required_confirmations}`);
            else if (r.message) setStatusMsg(r.message);
          } catch (recheckErr) { console.debug("recheck transient error", recheckErr?.message); }
        }
      } catch (pollErr) { console.debug("payment poll transient error", pollErr?.message); }
    }, 5000);
    return () => clearInterval(t);
  }, [order?.id, phase, cfg?.required_confirmations]);

  const doCreateOrder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coupons/buy", { variant });
      setOrder(data); setCoupon(null); setTxHash(""); setPhase("idle");
      toast.success("Order created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doConnect = async () => {
    setPhase("connecting"); setBusy(true);
    try {
      const addr = await connectWallet();
      setWallet(addr);
      const cid = await currentChainId();
      setConnectedChain(cid);
      toast.success("Wallet connected");
      // Ensure correct chain
      if (cfg && cid !== cfg.chain_id) {
        toast.info("Switching to BNB Smart Chain...");
        await switchToChain(cfg.chain_id);
        setConnectedChain(cfg.chain_id);
      }
    } catch (e) {
      toast.error(e.message || "Wallet connection failed");
    } finally { setBusy(false); setPhase("idle"); }
  };

  const doPay = async () => {
    if (!wallet || !order || !cfg) return;
    if (connectedChain !== cfg.chain_id) {
      try { await switchToChain(cfg.chain_id); setConnectedChain(cfg.chain_id); }
      catch (e) { return toast.error("Please switch your wallet to BNB Smart Chain first."); }
    }
    setBusy(true); setPhase("signing"); setStatusMsg("Confirm the transaction in your wallet...");
    try {
      const hash = await sendUsdtTransfer({
        from: wallet,
        contract: order.usdt_contract,
        recipient: order.receiving_wallet,
        amount: order.amount_usdt,
        decimals: order.usdt_decimals,
      });
      setTxHash(hash);
      setPhase("verifying");
      setStatusMsg("Transaction broadcast — verifying on-chain...");
      const { data } = await api.post("/coupons/submit-payment", {
        order_id: order.id, tx_hash: hash, sender_wallet: wallet,
      });
      if (data.status === "paid") {
        setPhase("paid");
        setStatusMsg("Payment confirmed — Crown Coupon issued");
        toast.success("Payment confirmed!");
        const { data: fresh } = await api.get(`/coupons/order/${order.id}`);
        setOrder(fresh.order); setCoupon(fresh.coupon);
      } else if (data.message) {
        setStatusMsg(data.message);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Payment failed";
      if (String(e?.code) === "4001" || /user rejected/i.test(String(msg))) {
        setPhase("idle"); toast.error("Transaction cancelled in wallet");
      } else {
        setPhase("failed"); setStatusMsg(formatApiError(msg));
        toast.error(formatApiError(msg));
      }
    } finally { setBusy(false); }
  };

  const recheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/coupons/order/${order.id}/recheck`);
      if (data.status === "paid") {
        setPhase("paid"); setStatusMsg("Payment confirmed");
        const { data: fresh } = await api.get(`/coupons/order/${order.id}`);
        setOrder(fresh.order); setCoupon(fresh.coupon);
      } else if (data.confirmations !== undefined) setStatusMsg(`Confirmations: ${data.confirmations} / ${cfg?.required_confirmations}`);
      else if (data.reason) { setPhase("failed"); setStatusMsg(data.reason); }
      else if (data.message) setStatusMsg(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fmtSec = (s) => s == null ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const explorer = order?.explorer_url || cfg?.explorer_url || "https://bscscan.com";
  const chainMismatch = wallet && cfg && connectedChain != null && connectedChain !== cfg.chain_id;
  const testMode = (order?.test_mode ?? cfg?.test_mode) ?? true;

  return (
    <DashboardShell>
      <div className="mb-6">
        <div className="overline">Purchase flow</div>
        <h1 className="font-serif text-4xl">Buy Crown Coupon</h1>
      </div>

      {testMode && (
        <div className="mb-6 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm p-4 flex items-start gap-3" data-testid="banner-testmode">
          <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">TEST MODE is ON</div>
            <div className="text-xs mt-1">Real payments are disabled. Do not send real USDT. Admin manually verifies simulated orders. Toggle this off in <em>Admin → Settings → Payment</em> before going live.</div>
          </div>
        </div>
      )}

      {!testMode && (
        <div className="mb-6 rounded-md bg-blue-50 border border-blue-200 text-blue-900 text-sm p-4 flex items-start gap-3" data-testid="banner-live">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Verify the address and network before signing</div>
            <div className="text-xs mt-1">This is a live BNB Smart Chain payment. Never send funds to any address other than the one shown below. We will never ask for your seed phrase or private keys.</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="crown-card p-6">
          <div className="overline">Item</div>
          <div className="flex items-center justify-between mt-2">
            <div>
              <div className="font-serif text-3xl">Crown Coupon</div>
              <div className="text-slate-300 text-sm mt-1">USDT · BEP-20 · BNB Smart Chain</div>
            </div>
            <div className="text-right">
              <div className="font-serif text-3xl tabular"><UsdtAmount value={priceForVariant} decimals={0} /></div>
              <div className="text-xs text-slate-400 mt-1">Chain ID {cfg?.chain_id ?? "—"}</div>
            </div>
          </div>
          {!order && (
            <div className="mt-5" data-testid="variant-selector">
              <div className="overline mb-2">Choose coupon</div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setVariant("1000")}
                  data-testid="variant-1000"
                  className={`text-left p-3 rounded-md border transition ${variant === "1000" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/25"}`}
                >
                  <div className="text-xs text-slate-400">Flagship</div>
                  <div className="font-serif text-xl tabular text-[#D4AF37]">1,000 USDT</div>
                  <div className="text-[10px] text-slate-400 mt-1">Numbered · referral rewards</div>
                </button>
                <button
                  type="button"
                  onClick={() => setVariant("100")}
                  data-testid="variant-100"
                  className={`text-left p-3 rounded-md border transition ${variant === "100" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/25"}`}
                >
                  <div className="text-xs text-slate-400">Companion</div>
                  <div className="font-serif text-xl tabular text-[#D4AF37]">100 USDT</div>
                  <div className="text-[10px] text-slate-400 mt-1">Activation only · no rewards</div>
                </button>
                <button
                  type="button"
                  onClick={() => setVariant("50")}
                  data-testid="variant-50"
                  className={`text-left p-3 rounded-md border transition ${variant === "50" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/25"}`}
                >
                  <div className="text-xs text-slate-400">Companion</div>
                  <div className="font-serif text-xl tabular text-[#D4AF37]">50 USDT</div>
                  <div className="text-[10px] text-slate-400 mt-1">Activation only · no rewards</div>
                </button>
              </div>
            </div>
          )}
          <div className="mt-6 border-t border-[#556B2F]/10 pt-4 space-y-3 text-sm">
            <div className="flex items-center gap-2 text-slate-300"><CheckCircle2 className="h-4 w-4 text-[#556B2F]" /> Numbered coupon issued after on-chain verification</div>
            <div className="flex items-center gap-2 text-slate-300"><CheckCircle2 className="h-4 w-4 text-[#556B2F]" /> One coupon per confirmed payment · idempotent</div>
            <div className="flex items-center gap-2 text-slate-300"><CheckCircle2 className="h-4 w-4 text-[#556B2F]" /> Non-custodial · we never see your keys</div>
          </div>
        </div>

        <div className="crown-card p-6">
          <div className="overline">Payment</div>
          {!order && (
            <>
              <p className="mt-2 text-sm text-slate-300">Create an order — you'll have {cfg?.order_expiration_minutes ?? 30} minutes to complete payment from your wallet.</p>
              <Button onClick={doCreateOrder} disabled={busy} className="mt-4 bg-[#556B2F] hover:bg-[#2A331A] text-white" data-testid="btn-create-order">Create order</Button>
            </>
          )}

          {order && (
            <div className="mt-3 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-slate-400">Order ID</div>
                  <div className="font-medium tabular" data-testid="order-id">{order.id}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">Expires in</div>
                  <div className={`font-medium tabular ${timeLeft === 0 ? "text-red-600" : ""}`} data-testid="order-expires">{fmtSec(timeLeft)}</div>
                </div>
              </div>

              <div className="rounded-md border border-[#556B2F]/20 p-3 text-xs bg-[#1B3455]/40 space-y-1">
                <div className="flex justify-between"><span className="text-slate-400">Amount</span><span className="tabular font-medium"><UsdtAmount value={order.amount_usdt} decimals={0} /></span></div>
                <div className="flex justify-between"><span className="text-slate-400">Network</span><span>BNB Smart Chain (id {order.chain_id})</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Token</span><span>USDT (BEP-20)</span></div>
                <div className="flex justify-between items-start"><span className="text-slate-400">USDT contract</span><span className="tabular text-[10px] break-all max-w-[220px]">{order.usdt_contract}</span></div>
                <div className="flex justify-between items-start"><span className="text-slate-400">To (merchant)</span><span className="tabular text-[10px] break-all max-w-[220px]" data-testid="merchant-wallet">{order.receiving_wallet || <em className="text-red-600">not configured</em>}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Required confirmations</span><span>{order.required_confirmations}</span></div>
              </div>

              {!wallet ? (
                <Button onClick={doConnect} disabled={busy} className="w-full bg-[#2A331A] hover:bg-[#000] text-white" data-testid="btn-connect-wallet">
                  <Wallet className="h-4 w-4 mr-2" /> Connect wallet
                </Button>
              ) : (
                <div className="text-xs text-slate-300">
                  Connected: <span className="tabular font-medium" data-testid="connected-wallet">{wallet.slice(0, 6)}...{wallet.slice(-4)}</span>
                  {chainMismatch && (
                    <div className="mt-2 text-red-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Wrong network — <button onClick={() => switchToChain(cfg.chain_id).then(() => setConnectedChain(cfg.chain_id))} className="underline">switch to BNB Smart Chain</button></div>
                  )}
                </div>
              )}

              {order.status === "pending" && wallet && !chainMismatch && order.receiving_wallet && (
                <>
                  <div className="text-xs text-slate-300 border-l-2 border-[#D4AF37] pl-3">Please <strong>verify the recipient address and network</strong> in your wallet before confirming.</div>
                  <Button onClick={doPay} disabled={busy} className="w-full bg-[#3B4A1F] hover:bg-[#212814] text-white" data-testid="btn-pay">
                    {busy && phase === "signing" ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Waiting for wallet...</> : `Pay ${formatUsdt(order.amount_usdt, { decimals: 0 })}`}
                  </Button>
                </>
              )}

              {order.status === "pending" && !order.receiving_wallet && (
                <div className="rounded-md bg-red-50 border border-red-200 text-red-800 text-xs p-3">
                  Merchant receiving wallet is not configured. Please contact support before paying.
                </div>
              )}

              {(phase === "verifying" || order.status === "verifying") && (
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                  <div className="flex items-center gap-2 font-medium"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying on-chain</div>
                  {(txHash || order.tx_hash) && (
                    <div className="mt-1 tabular break-all">tx: <a href={`${explorer}/tx/${txHash || order.tx_hash}`} target="_blank" rel="noreferrer" className="underline" data-testid="link-explorer-verifying">{txHash || order.tx_hash}</a></div>
                  )}
                  {statusMsg && <div className="mt-1">{statusMsg}</div>}
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={recheck} disabled={busy} className="text-blue-700 underline flex items-center gap-1" data-testid="btn-recheck"><RefreshCw className="h-3 w-3" /> Refresh status</button>
                    <Link to={`/app/order/${order.id}`} className="text-blue-700 underline">Open status page</Link>
                  </div>
                </div>
              )}

              {phase === "paid" || order.status === "paid" ? (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-emerald-900" data-testid="payment-confirmed">
                  <div className="font-semibold flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> Payment Confirmed</div>
                  <div className="mt-2 text-xs">1 Active Crown Coupon ({formatUsdt(1000, { decimals: 0 })})</div>
                  {(coupon?.id || order.coupon_id) && (
                    <div className="mt-2">Coupon: <span className="font-mono font-semibold tabular">{coupon?.id || order.coupon_id}</span></div>
                  )}
                  {(txHash || order.tx_hash) && (
                    <div className="mt-2 text-xs break-all">tx: <a href={`${explorer}/tx/${txHash || order.tx_hash}`} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1" data-testid="link-explorer-paid">{txHash || order.tx_hash} <ExternalLink className="h-3 w-3" /></a></div>
                  )}
                </div>
              ) : null}

              {(phase === "failed" || order.status === "failed") && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-800">
                  <div className="font-semibold">Verification failed</div>
                  <div className="mt-1">{statusMsg || order.reason || "Payment could not be verified."}</div>
                  <Button variant="outline" onClick={doCreateOrder} className="mt-3" data-testid="btn-new-order">Create a new order</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

export function OrderStatus() {
  const { id } = useParams();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/coupons/order/${id}`); setState(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [id]);

  const recheck = async () => {
    setBusy(true);
    try { await api.post(`/coupons/order/${id}/recheck`); await load(); toast.success("Rechecked"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!state) return <DashboardShell><div className="text-slate-400">Loading...</div></DashboardShell>;
  const o = state.order;
  const c = state.coupon;
  const explorer = o.explorer_url || "https://bscscan.com";
  return (
    <DashboardShell>
      <div className="mb-6"><div className="overline">Transaction status</div><h1 className="font-serif text-4xl" data-testid="status-order-id">{o.id}</h1></div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="crown-card p-6 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Payment status</span><StatusChip status={o.status} /></div>
          <div className="flex justify-between"><span className="text-slate-400">Coupon status</span><span data-testid="status-coupon-id">{c ? <StatusChip status={c.status} /> : <span className="text-slate-400">—</span>}</span></div>
          {c && <div className="flex justify-between"><span className="text-slate-400">Coupon ID</span><span className="tabular font-medium">{c.id}</span></div>}
          <div className="flex justify-between"><span className="text-slate-400">Amount</span><span className="tabular"><UsdtAmount value={o.amount_usdt} decimals={0} /></span></div>
          <div className="flex justify-between"><span className="text-slate-400">Network</span><span>BNB Smart Chain (id {o.chain_id})</span></div>
          <div className="flex justify-between items-start"><span className="text-slate-400">Wallet (sender)</span><span className="tabular text-[10px] break-all max-w-[220px]">{o.sender_wallet || "—"}</span></div>
          <div className="flex justify-between items-start"><span className="text-slate-400">Merchant (recipient)</span><span className="tabular text-[10px] break-all max-w-[220px]">{o.receiving_wallet || "—"}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Confirmations</span><span className="tabular">{o.confirmations ?? 0} / {o.required_confirmations}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Test mode</span><span>{o.test_mode ? "Yes" : "No"}</span></div>
        </div>
        <div className="crown-card p-6 space-y-3">
          <div className="overline">Transaction</div>
          {o.tx_hash ? (
            <>
              <div className="text-xs tabular break-all" data-testid="status-tx-hash">{o.tx_hash}</div>
              <a href={`${explorer}/tx/${o.tx_hash}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm link-btn" data-testid="btn-open-explorer">
                <ExternalLink className="h-4 w-4" /> View transaction on BscScan
              </a>
            </>
          ) : (
            <>
              <div className="text-sm text-slate-400">No transaction submitted yet.</div>
              <a
                href={o.receiving_wallet ? `${explorer}/address/${o.receiving_wallet}` : explorer}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm link-btn"
                data-testid="btn-open-explorer"
              >
                <ExternalLink className="h-4 w-4" /> Open BscScan
              </a>
            </>
          )}
          <Button
            onClick={recheck}
            disabled={busy || o.status === "paid" || o.test_mode || !o.tx_hash}
            variant="outline"
            data-testid="btn-status-recheck"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Re-verify on chain
          </Button>
          {o.status === "paid" && (
            <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-emerald-900 text-sm">
              <div className="font-semibold">Payment Confirmed</div>
              <div className="text-xs">1 Active Crown Coupon (1,000 USDT) — <span className="font-mono font-semibold">{o.coupon_id}</span></div>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

export function MyCoupons() {
  const { data: rows, error, loading, refetch } = useDashboardFetch(
    async () => (await api.get("/me/coupons")).data
  );
  return (
    <DashboardShell>
      <h1 className="font-serif text-4xl mb-6">My Coupons</h1>
      {error && !rows ? <LoadErrorState error={error} onRetry={refetch} testid="coupons-error" /> : (
      <>
      {(() => {
        const flagship = (rows || []).filter((r) => (r.variant || "1000") === "1000");
        const companion = (rows || []).filter((r) => r.variant === "100");
        const companion50 = (rows || []).filter((r) => r.variant === "50");
        const groups = [
          { key: "1000", title: "1,000 USDT Crown Coupon", subtitle: "Flagship · numbered · referral rewards", rows: flagship },
          { key: "100", title: "100 USDT Crown Coupon", subtitle: "Companion · activation only", rows: companion },
          { key: "50", title: "50 USDT Crown Coupon", subtitle: "Companion · activation only", rows: companion50 },
        ];
        return groups.map((g) => (
          <div key={g.key} className="crown-card p-6 mb-6" data-testid={`coupons-group-${g.key}`}>
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="overline">{g.subtitle}</div>
                <div className="font-serif text-2xl mt-1">{g.title}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400">Active coupons</div>
                <div className="stat-number" data-testid={`coupons-count-${g.key}`}>{g.rows.length}</div>
              </div>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Coupon ID</TableHead><TableHead>Purchase Date</TableHead><TableHead>Price</TableHead><TableHead>Tx Hash</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {loading && !rows && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Loading...</TableCell></TableRow>}
                {rows && g.rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No {g.key === "50" ? "50" : (g.key === "100" ? "100" : "1,000")} USDT coupons yet</TableCell></TableRow>}
                {g.rows.map((r) => (
                  <TableRow key={r.id} data-testid={`coupon-row-${r.id}`}>
                    <TableCell className="font-medium tabular">{r.id}</TableCell>
                    <TableCell>{new Date(r.purchase_date).toLocaleString()}</TableCell>
                    <TableCell className="tabular"><UsdtAmount value={r.price_usdt} decimals={0} /></TableCell>
                    <TableCell className="text-xs tabular truncate max-w-[220px]">{r.tx_hash}</TableCell>
                    <TableCell><StatusChip status={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ));
      })()}
      </>
      )}
    </DashboardShell>
  );
}

export function Referrals() {
  const { user } = useAuth();
  const { data, error, loading, refetch } = useDashboardFetch(
    async () => (await api.get("/me/referrals")).data
  );
  const link = `${window.location.origin}/register?ref=${user?.referral_id || ""}`;
  const direct = data?.direct ?? [];
  const team = data?.team || {};
  const lc = team.level_counts || {};
  const tree = team.tree || {};
  return (
    <DashboardShell>
      <h1 className="font-serif text-4xl mb-2">My Team</h1>
      <p className="text-slate-300 mb-6">Share your Crown Coupon referral link. You'll earn commissions only on your invitees' genuine purchases.</p>

      <div className="crown-card p-5 mb-6 flex gap-2">
        <Input readOnly value={link} data-testid="referrals-link" />
        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
      </div>

      {/* Team snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard testid="my-team-direct" label="Direct Referrals" value={team.direct_referrals_count ?? 0} />
        <StatCard testid="my-team-total" label="Total Downline" value={team.total_downline ?? 0} />
        <StatCard testid="my-team-active" label="Active" value={team.active_members ?? 0} sub="Members" />
        <StatCard testid="my-team-inactive" label="Inactive" value={team.inactive_members ?? 0} sub="Members" />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6 text-center">
        <div className="crown-card p-4"><div className="overline">Level 1</div><div className="stat-number mt-1" data-testid="my-team-l1">{lc["1"] ?? 0}</div></div>
        <div className="crown-card p-4"><div className="overline">Level 2</div><div className="stat-number mt-1" data-testid="my-team-l2">{lc["2"] ?? 0}</div></div>
        <div className="crown-card p-4"><div className="overline">Level 3</div><div className="stat-number mt-1" data-testid="my-team-l3">{lc["3"] ?? 0}</div></div>
      </div>

      {error && !data ? <LoadErrorState error={error} onRetry={refetch} testid="referrals-error" /> : (
      <div className="crown-card p-6">
        <div className="overline mb-2">Direct referrals</div>
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Joined</TableHead><TableHead>KYC</TableHead><TableHead>Status</TableHead><TableHead>Completed Sales</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading && !data && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Loading...</TableCell></TableRow>}
            {data && direct.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No referrals yet</TableCell></TableRow>}
            {direct.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.full_name}</TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                <TableCell><StatusChip status={r.kyc_status} /></TableCell>
                <TableCell><StatusChip status={r.status || "active"} /></TableCell>
                <TableCell className="tabular">{r.completed_sales}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      )}

      {/* Level 2 / Level 3 lists — read-only downline visibility */}
      {["2","3"].map((lv) => (
        (tree[lv] || []).length > 0 && (
          <div key={lv} className="crown-card p-6 mt-6" data-testid={`my-team-level-${lv}`}>
            <div className="overline mb-2">Level {lv} members ({(tree[lv] || []).length})</div>
            <div className="rounded-md border border-white/10 divide-y divide-white/5">
              {(tree[lv] || []).map((m) => (
                <div key={m.id} className="p-2 text-xs flex items-center justify-between">
                  <div>
                    <div className="font-medium">{m.full_name || m.email}</div>
                    <div className="text-slate-400">{m.email}</div>
                  </div>
                  <StatusChip status={m.status || "active"} />
                </div>
              ))}
            </div>
          </div>
        )
      ))}
    </DashboardShell>
  );
}

export function Commissions() {
  const { data: rows, error, loading, refetch } = useDashboardFetch(
    async () => (await api.get("/me/commissions")).data
  );
  return (
    <DashboardShell>
      <h1 className="font-serif text-4xl mb-6">Commission History</h1>
      {error && !rows ? <LoadErrorState error={error} onRetry={refetch} testid="commissions-error" /> : (
      <div className="crown-card p-6">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Order</TableHead><TableHead>Level</TableHead><TableHead>Sale</TableHead><TableHead>Rate</TableHead><TableHead>Commission</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading && !rows && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Loading...</TableCell></TableRow>}
            {rows && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No commissions</TableCell></TableRow>}
            {(rows || []).map((c) => (
              <TableRow key={c.id}>
                <TableCell className="text-xs">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-xs tabular">{c.source_order_id}</TableCell>
                <TableCell>L{c.level}</TableCell>
                <TableCell className="tabular"><UsdtAmount value={c.sale_amount} decimals={0} /></TableCell>
                <TableCell className="tabular">{c.rate}%</TableCell>
                <TableCell className="tabular font-medium"><UsdtAmount value={c.commission_amount} /></TableCell>
                <TableCell><StatusChip status={c.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      )}
    </DashboardShell>
  );
}

export function Withdrawals() {
  const [rows, setRows] = useState([]);
  const [addr, setAddr] = useState("");
  const [amount, setAmount] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [balance, setBalance] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [w, d] = await Promise.all([api.get("/me/withdrawals"), api.get("/me/dashboard")]);
      setRows(w.data);
      // approved - already used (any status not rejected)
      const outstanding = w.data
        .filter((x) => !["rejected"].includes(x.status))
        .reduce((s, x) => s + x.amount, 0);
      setBalance(d.data.stats.approved_commission - outstanding);
      setLoaded(true);
    } catch (e) {
      setLoadError(formatApiError(e?.response?.data?.detail, "Couldn't load withdrawals — please retry."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!confirm) return toast.error("Please confirm the withdrawal details");
    try {
      await api.post("/withdrawals/request", { amount: parseFloat(amount), wallet_address: addr });
      toast.success("Withdrawal request submitted");
      setAddr(""); setAmount(""); setConfirm(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <DashboardShell>
      <h1 className="font-serif text-4xl mb-6">Withdrawals</h1>
      {loadError && !loaded ? <LoadErrorState error={loadError} onRetry={load} testid="withdrawals-error" /> : (
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="crown-card p-6 lg:col-span-1">
          <div className="overline">Available balance</div>
          <div className="font-serif text-4xl mt-2 tabular" data-testid="wd-balance"><UsdtAmount value={balance} /></div>
          <div className="text-xs text-slate-400 mt-1">Approved commissions</div>
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="wd-form">
            <div>
              <Label>USDT BEP-20 wallet address</Label>
              <Input required value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="0x..." data-testid="wd-address" />
            </div>
            <div>
              <Label>Amount (USDT)</Label>
              <Input required type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="wd-amount" />
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
              <div>Network: <strong>BNB Smart Chain</strong></div>
              <div>Token: <strong>USDT (BEP-20)</strong></div>
              <div>Amount: <strong className="tabular">{amount ? formatUsdt(amount) : "—"}</strong></div>
              <div className="truncate">To: <strong className="tabular">{addr || "—"}</strong></div>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="wd-confirm" /> I confirm the details are correct
              </label>
            </div>
            <Button disabled={!confirm} className="w-full bg-[#556B2F] text-white" data-testid="wd-submit">Request withdrawal</Button>
          </form>
        </div>
        <div className="crown-card p-6 lg:col-span-2">
          <div className="overline mb-3">History</div>
          <Table>
            <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Date</TableHead><TableHead>Amount</TableHead><TableHead>Wallet</TableHead><TableHead>Tx</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No withdrawals</TableCell></TableRow>}
              {rows.map((w) => (
                <TableRow key={w.id}>
                  <TableCell className="tabular text-xs">{w.id}</TableCell>
                  <TableCell className="text-xs">{new Date(w.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="tabular"><UsdtAmount value={w.amount} /></TableCell>
                  <TableCell className="tabular text-xs truncate max-w-[140px]">{w.wallet_address}</TableCell>
                  <TableCell className="tabular text-xs truncate max-w-[140px]">{w.tx_hash || "—"}</TableCell>
                  <TableCell><StatusChip status={w.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      )}
    </DashboardShell>
  );
}

export function KYCPage() {
  const [rec, setRec] = useState(null);
  const [docType, setDocType] = useState("passport");
  const [dob, setDob] = useState("");
  const [idFile, setIdFile] = useState(null);
  const [addrFile, setAddrFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const { data } = await api.get("/kyc/me");
      setRec(data);
    } catch (e) {
      setLoadError(formatApiError(e?.response?.data?.detail, "Couldn't load KYC status — please retry."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!idFile || !addrFile) return toast.error("Both files required");
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("dob", dob);
    fd.append("id_file", idFile);
    fd.append("address_file", addrFile);
    setLoading(true);
    try {
      await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("KYC submitted for review");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <DashboardShell>
      <h1 className="font-serif text-4xl mb-2">KYC Verification</h1>
      <p className="text-slate-300 mb-6">Complete KYC to unlock higher trust levels. Documents are stored securely.</p>

      {loadError && !rec ? <LoadErrorState error={loadError} onRetry={load} testid="kyc-error" /> : (<>
      <div className="crown-card p-6 mb-6">
        <div className="overline">Current status</div>
        <div className="mt-2"><StatusChip status={rec?.status || "not_submitted"} /></div>
        {rec?.reason && <div className="mt-2 text-sm text-red-700">Reason: {rec.reason}</div>}
      </div>

      {(!rec || rec.status !== "approved") && (
        <form onSubmit={submit} className="crown-card p-6 space-y-4 max-w-2xl" data-testid="kyc-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Document type</Label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-input px-3 text-sm" data-testid="kyc-doctype">
                <option value="passport">Passport</option>
                <option value="national_id">National ID</option>
                <option value="drivers_license">Driver's License</option>
              </select>
            </div>
            <div>
              <Label>Date of birth</Label>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="mt-1" data-testid="kyc-dob" />
            </div>
          </div>
          <div>
            <Label>Government ID (image or PDF)</Label>
            <Input type="file" accept="image/*,application/pdf" onChange={(e) => setIdFile(e.target.files[0])} className="mt-1" data-testid="kyc-id-file" />
          </div>
          <div>
            <Label>Address proof (image or PDF)</Label>
            <Input type="file" accept="image/*,application/pdf" onChange={(e) => setAddrFile(e.target.files[0])} className="mt-1" data-testid="kyc-addr-file" />
          </div>
          <Button disabled={loading} type="submit" className="bg-[#556B2F] text-white" data-testid="kyc-submit">{loading ? "Uploading..." : "Submit for review"}</Button>
        </form>
      )}
      </>)}
    </DashboardShell>
  );
}

export function Support() {
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ category: "General", subject: "", message: "" });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data);
    } catch (e) {
      setLoadError(formatApiError(e?.response?.data?.detail, "Couldn't load tickets — please retry."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/support/tickets", form);
      setForm({ category: "General", subject: "", message: "" });
      toast.success("Ticket created");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openTicket = async (id) => {
    const { data } = await api.get(`/support/tickets/${id}`);
    setSelected(data);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/support/tickets/${selected.id}/reply`, { message: reply });
      setReply("");
      openTicket(selected.id);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DashboardShell>
      <h1 className="font-serif text-4xl mb-6">Support</h1>
      {loadError && tickets.length === 0 ? (
        <LoadErrorState error={loadError} onRetry={load} testid="support-error" />
      ) : (
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="crown-card p-6">
          <div className="overline mb-3">Create ticket</div>
          <form onSubmit={create} className="space-y-3" data-testid="ticket-form">
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-input px-3 text-sm">
                <option>General</option><option>Payment</option><option>Withdrawal</option><option>KYC</option><option>Other</option>
              </select>
            </div>
            <div>
              <Label>Subject</Label>
              <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1" data-testid="ticket-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" rows={4} data-testid="ticket-message" />
            </div>
            <Button type="submit" className="w-full bg-[#556B2F] text-white" data-testid="ticket-submit">Submit</Button>
          </form>
        </div>

        <div className="crown-card p-6 lg:col-span-2">
          <div className="overline mb-3">Your tickets</div>
          <div className="space-y-2 max-h-64 overflow-auto">
            {tickets.length === 0 && <div className="text-slate-400 text-sm">No tickets</div>}
            {tickets.map((t) => (
              <button key={t.id} onClick={() => openTicket(t.id)} className={`w-full text-left p-3 rounded-md border ${selected?.id === t.id ? "border-[#556B2F] bg-[#556B2F]/5" : "border-slate-200"}`} data-testid={`ticket-item-${t.id}`}>
                <div className="flex justify-between items-center">
                  <div><div className="font-medium">{t.subject}</div><div className="text-xs text-slate-400 tabular">{t.id} · {t.category}</div></div>
                  <StatusChip status={t.status} />
                </div>
              </button>
            ))}
          </div>
          {selected && (
            <div className="mt-6 border-t pt-4">
              <div className="mb-2 font-semibold">{selected.subject}</div>
              <div className="space-y-3 max-h-60 overflow-auto pr-2">
                {selected.messages.map((m, i) => (
                  <div key={`${m.at}-${m.author}-${i}`} className={`p-3 rounded-md ${m.from === "admin" ? "bg-[#556B2F]/5" : "bg-[#1B3455]/40"}`}>
                    <div className="text-xs text-slate-400 mb-1">{m.author} · {new Date(m.at).toLocaleString()}</div>
                    <div className="text-sm">{m.message}</div>
                  </div>
                ))}
              </div>
              {selected.status !== "closed" && (
                <div className="mt-3 flex gap-2">
                  <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply..." data-testid="ticket-reply" />
                  <Button onClick={sendReply} className="bg-[#556B2F] text-white" data-testid="ticket-send">Send</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </DashboardShell>
  );
}


// =============================================================
//  BOOM BOX  ·  User dashboard page
// =============================================================
// User-facing terminology: never "Admin" — always "Crown Coupon Company".
export function BoomBox() {
  const { data, error, loading, refetch } = useDashboardFetch(
    async () => (await api.get("/boombox/status")).data
  );
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | signing | verifying | active
  const [wallet, setWallet] = useState("");
  const [chain, setChain] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [wdAmount, setWdAmount] = useState("");
  const [wdAddr, setWdAddr] = useState("");
  const [tab, setTab] = useState("balance"); // "balance" | "withdrawal"

  useEffect(() => { api.get("/public/payment-config").then(({ data }) => setCfg(data)); }, []);
  useEffect(() => {
    if (!window.ethereum) return;
    window.ethereum.on?.("accountsChanged", (a) => setWallet(a?.[0] || ""));
    window.ethereum.on?.("chainChanged", (c) => setChain(parseInt(c, 16)));
  }, []);

  const sub = data?.subscription;
  const bal = data?.balance;
  const boomboxCfg = data?.config;
  const active = sub?.status === "active";
  const explorer = sub?.explorer_url || cfg?.explorer_url || "https://bscscan.com";
  const chainMismatch = wallet && cfg && chain && chain !== cfg.chain_id;

  // Poll every 5s while a payment is verifying
  useEffect(() => {
    if (sub?.status !== "verifying") return;
    const t = setInterval(refetch, 5000);
    return () => clearInterval(t);
  }, [sub?.status, refetch]);

  const doSubscribe = async () => {
    setBusy(true);
    try {
      await api.post("/boombox/subscribe", {});
      toast.success("Subscription order created");
      await refetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doConnect = async () => {
    setBusy(true);
    try {
      const addr = await connectWallet();
      setWallet(addr);
      const cid = await currentChainId();
      setChain(cid);
      if (cfg && cid !== cfg.chain_id) {
        await switchToChain(cfg.chain_id); setChain(cfg.chain_id);
      }
      toast.success("Wallet connected");
    } catch (e) { toast.error(e?.message || "Failed to connect"); }
    finally { setBusy(false); }
  };

  const doDeposit = async () => {
    if (!sub?.id || sub.status === "active") return;
    setBusy(true); setPhase("signing");
    try {
      const hash = await sendUsdtTransfer({
        from: wallet,
        contract: sub.usdt_contract,
        recipient: sub.receiving_wallet,
        amount: sub.amount_usdt,
        decimals: sub.usdt_decimals,
      });
      setTxHash(hash);
      setPhase("verifying");
      await api.post("/boombox/subscribe/submit-payment", {
        subscription_id: sub.id, tx_hash: hash, sender_wallet: wallet,
      });
      toast.success("Payment submitted — verifying");
      await refetch();
    } catch (e) {
      setPhase("idle");
      toast.error(formatApiError(e.response?.data?.detail, e?.message || "Payment failed"));
    } finally { setBusy(false); }
  };

  const doWithdraw = async () => {
    setBusy(true);
    try {
      const amt = parseFloat(wdAmount);
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Enter a valid amount");
      await api.post("/boombox/withdraw", { amount: amt, wallet_address: wdAddr });
      toast.success("Withdrawal request submitted — awaiting Crown Coupon Company approval");
      setWdAmount(""); setWdAddr("");
      await refetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, e?.message)); }
    finally { setBusy(false); }
  };

  if (loading && !data) return <DashboardShell><div className="crown-card p-6 text-slate-300">Loading Boom Box…</div></DashboardShell>;
  if (error && !data) return <DashboardShell><LoadErrorState error={error} onRetry={refetch} testid="boombox-error" /></DashboardShell>;

  return (
    <DashboardShell>
      <div className="mb-6">
        <div className="overline">Boom Box</div>
        <h1 className="font-serif text-4xl mt-2">Boom Box Subscription</h1>
        <p className="text-slate-400 text-sm mt-1">Separate from your Crown Coupons — pay a 10 USDT subscription to unlock your Boom Box account.</p>
      </div>

      {/* Subscription card */}
      <div className="crown-card p-6 mb-6" data-testid="bb-subscription-card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="overline">Subscription Fee</div>
            <div className="stat-number mt-2" data-testid="bb-sub-fee">{formatUsdt(boomboxCfg?.subscription_fee_usdt ?? 10, { decimals: 2 })}</div>
            <div className="overline mt-5">Subscription Amount</div>
            <div className="stat-number mt-2" data-testid="bb-sub-amount">{formatUsdt(sub?.subscription_amount_usdt ?? 0, { decimals: 2 })}</div>
            <div className="overline mt-5">Status</div>
            <div className="mt-2" data-testid="bb-status"><StatusChip status={sub?.status || "inactive"} /></div>
          </div>
          <div>
            <div className="overline">Validity</div>
            <div className="stat-number mt-2">{boomboxCfg?.validity_days ?? 30} Days</div>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div>
                <div className="overline">Start</div>
                <div className="mt-2 text-sm text-white" data-testid="bb-start">{sub?.started_at ? new Date(sub.started_at).toLocaleDateString() : "—"}</div>
              </div>
              <div>
                <div className="overline">Expiry</div>
                <div className="mt-2 text-sm text-white" data-testid="bb-expiry">{sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}</div>
              </div>
            </div>
            <div className="overline mt-5">Remaining Days</div>
            <div className="stat-number mt-2" data-testid="bb-remaining">{sub?.remaining_days ?? 0}</div>
          </div>
        </div>

        {/* Deposit / Payment flow */}
        {!active && (
          <div className="mt-6 border-t border-white/10 pt-5" data-testid="bb-deposit-panel">
            {!sub || sub.status === "expired" || sub.status === "failed" ? (
              <Button onClick={doSubscribe} disabled={busy} className="bg-[#556B2F] hover:bg-[#2A331A] text-white" data-testid="btn-bb-start">Deposit 10 USDT</Button>
            ) : sub.status === "pending" ? (
              <div className="space-y-3">
                <div className="text-xs rounded-md border border-white/10 p-3 space-y-1">
                  <div className="flex justify-between"><span className="text-slate-400">Order</span><span className="tabular">{sub.id}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Amount</span><span className="tabular">{formatUsdt(sub.amount_usdt, { decimals: 2 })}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Network</span><span>BNB Smart Chain</span></div>
                  <div className="flex justify-between items-start"><span className="text-slate-400">Crown Coupon Company Wallet</span><span className="tabular text-[10px] break-all max-w-[220px]">{sub.receiving_wallet || <em className="text-red-400">not configured</em>}</span></div>
                </div>
                {!wallet ? (
                  <Button onClick={doConnect} disabled={busy} className="bg-[#2A331A] hover:bg-black text-white" data-testid="btn-bb-connect"><Wallet className="h-4 w-4 mr-2" /> Connect wallet</Button>
                ) : chainMismatch ? (
                  <div className="text-xs text-red-400 flex items-center gap-2"><AlertTriangle className="h-3 w-3" /> Wrong network — <button className="underline" onClick={() => switchToChain(cfg.chain_id).then(() => setChain(cfg.chain_id))}>switch to BNB Smart Chain</button></div>
                ) : (
                  <Button onClick={doDeposit} disabled={busy || !sub.receiving_wallet} className="bg-[#3B4A1F] hover:bg-[#212814] text-white" data-testid="btn-bb-pay">
                    {phase === "signing" ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Waiting for wallet…</> : `Pay ${formatUsdt(sub.amount_usdt, { decimals: 2 })}`}
                  </Button>
                )}
              </div>
            ) : sub.status === "verifying" ? (
              <div className="rounded-md bg-blue-500/10 border border-blue-400/30 p-3 text-xs text-blue-200">
                <div className="flex items-center gap-2 font-medium"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying on-chain — awaiting confirmations{sub.test_mode ? " (Test Mode — Crown Coupon Company will verify)" : ""}</div>
                {(txHash || sub.tx_hash) && <div className="mt-1 tabular break-all">tx: <a href={`${explorer}/tx/${txHash || sub.tx_hash}`} target="_blank" rel="noreferrer" className="underline">{txHash || sub.tx_hash}</a></div>}
                <div className="mt-2"><button className="underline" onClick={refetch}>Refresh status</button></div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Balance + Withdrawal card */}
      <div className="crown-card p-6" data-testid="bb-balance-card">
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab("balance")} data-testid="tab-bb-balance" className={`px-4 py-2 rounded-md text-sm ${tab === "balance" ? "bg-[#556B2F] text-white" : "border border-white/10 text-slate-300"}`}>Balance</button>
          <button onClick={() => setTab("withdrawal")} data-testid="tab-bb-withdrawal" className={`px-4 py-2 rounded-md text-sm ${tab === "withdrawal" ? "bg-[#556B2F] text-white" : "border border-white/10 text-slate-300"}`}>Withdrawal</button>
        </div>

        {tab === "balance" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard testid="bb-available" label="Available Balance" value={formatUsdt(bal?.available_balance ?? 0, { decimals: 2 })} sub="USDT" />
              <StatCard testid="bb-hold" label="Withdrawal Pending / Hold" value={formatUsdt(bal?.hold_amount ?? 0, { decimals: 2 })} sub="USDT" />
              <StatCard testid="bb-credited" label="Total Credited" value={formatUsdt(bal?.total_credited ?? 0, { decimals: 2 })} sub="USDT" />
              <StatCard testid="bb-withdrawn" label="Total Withdrawn" value={formatUsdt(bal?.total_withdrawn ?? 0, { decimals: 2 })} sub="USDT" />
            </div>
            <div className="mt-3 text-xs text-slate-400">Remaining balance (available + hold): <span className="tabular text-white">{formatUsdt(bal?.remaining_balance ?? 0, { decimals: 2 })} USDT</span></div>
            <div className="mt-5 overline">Balance history</div>
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data?.ledger || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No activity yet</TableCell></TableRow>}
                {(data?.ledger || []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{({credit:"Company Credit","withdraw_hold":"Withdrawal held","withdraw_release":"Withdrawal released","withdraw_paid":"Withdrawal paid"})[r.type] || r.type}</TableCell>
                    <TableCell className="tabular"><UsdtAmount value={r.amount} decimals={2} /></TableCell>
                    <TableCell className="text-xs text-slate-400">{r.memo || r.reason || (r.tx_hash ? `tx ${String(r.tx_hash).slice(0,10)}…` : "")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {tab === "withdrawal" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="overline">Available Balance</div>
              <div className="stat-number mt-2">{formatUsdt(bal?.available_balance ?? 0, { decimals: 2 })}</div>
              <div className="mt-5 space-y-3">
                <div>
                  <Label>Withdrawal Amount (USDT)</Label>
                  <Input type="number" min={0} step="0.01" value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} data-testid="bb-wd-amount" />
                </div>
                <div>
                  <Label>Your BSC Wallet Address</Label>
                  <Input value={wdAddr} onChange={(e) => setWdAddr(e.target.value)} placeholder="0x…" data-testid="bb-wd-addr" />
                </div>
                <Button onClick={doWithdraw} disabled={busy || !wdAmount || !wdAddr} className="bg-[#556B2F] hover:bg-[#2A331A] text-white" data-testid="btn-bb-submit-withdrawal">Submit Withdrawal Request</Button>
                <div className="text-xs text-slate-400">Requested amount goes on hold until Crown Coupon Company reviews it.</div>
                {boomboxCfg?.kyc_required_for_withdrawal && <div className="text-xs text-amber-400">Crown Coupon Company currently requires KYC approval for Boom Box withdrawals.</div>}
              </div>
            </div>
            <div>
              <div className="overline">Recent withdrawal requests</div>
              <Table>
                <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Tx</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(data?.withdrawals || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No requests yet</TableCell></TableRow>}
                  {(data?.withdrawals || []).map((w) => (
                    <TableRow key={w.id}>
                      <TableCell className="text-xs">{new Date(w.created_at).toLocaleString()}</TableCell>
                      <TableCell className="tabular"><UsdtAmount value={w.amount} decimals={2} /></TableCell>
                      <TableCell><StatusChip status={w.status} /></TableCell>
                      <TableCell className="text-xs tabular truncate max-w-[140px]">{w.tx_hash || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
